import { Box, Button } from "@mui/material";
import React from "react";
import Select, { components } from "react-select";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import "./opportunity-creation-rate.scss";

interface OpportunityCreationRateProps {
  isLoading?: boolean; // Define the type of isLoading if known
  metricsData: any;
  exportChart?: (params: any, data?: any) => void;
  enableExport?: boolean;
}
const OpportunityCreationRate: React.FC<OpportunityCreationRateProps> = ({
  isLoading,
  metricsData,
  exportChart,
  enableExport = false,
}) => {
  const [format, setFormat] = React.useState({ value: "PDF", label: "PDF" });

  const opportunityCreationRate = metricsData?.opportunityCreationRate || [];

  const seriesData: any[] = [];
  const categories: string[] = [];
  opportunityCreationRate.forEach((campaign: any) => {
    seriesData.push(Number(campaign.opportunityCreationRate.replace("%", "")));
    categories.push(campaign.name);
  });

  const labelColors: any[] = seriesData?.length
    ? seriesData.map((value: number) => (value === 0 ? "#6488EA" : "#fff"))
    : [];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: true,
      },
      id: "opportunityCreationRate",
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#7B8A8B",
        fontSize: "18px",
        fontFamily: "Roboto, sans-serif",
      },
    },
    plotOptions: {
      bar: {
        distributed: true,
        horizontal: false,
        columnWidth: "50%",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => (val === 0 ? val.toString() : ""),
      style: {
        colors: labelColors,
      },
    },
    xaxis: {
      categories: categories.slice(0, 5) || [],
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
        style: {
          fontSize: "14px",
        },
      },
      title: {
        text: "Campaigns",
        style: {
          fontSize: "14px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          fontSize: "14px",
          colors: ["black"],
        },
      },
      title: {
        text: "Opportunity Creation Rate (%)",
        style: {
          fontSize: "14px",
          fontWeight: 400,
        },
        offsetX: -8,
      },
    },
    colors: ["#6488EA", "#8E44AD", "#1C2F8D", "#BD3D44", "#ED5481", "#ED5481"],
    legend: {
      show: false,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const val = series[seriesIndex][dataPointIndex];
        const campaign = opportunityCreationRate[dataPointIndex];

        return `
          <div>
          <div style="margin-bottom: 4px; background-color:#f5f5f5; padding:5px; border-bottom: 1px solid #ddd;">
                ${campaign.name}
              </div>
            <div style="padding: 10px; font-family: Roboto, sans-serif; font-size: 14px; color: #333;">
              <div style="margin-bottom: 4px;">
                <span>Opportunity Creation Rate:</span>
                <strong> ${val}%</strong>
              </div>
              <div style="margin-bottom: 4px;">
                <span>Total Leads:</span> ${campaign?.totalLeads ?? 0}
              </div>
              <div style="margin-bottom: 4px;">
                <span>Converted Leads:</span> ${campaign?.convertedLeads ?? 0}
              </div>
            </div>
          </div>
          `;
      },
    },
    grid: {
      show: false,
    },
  };

  const series: any = [
    {
      name: "opportunity creation rate",
      data: seriesData.slice(0, 5) || [],
    },
  ];

  const fullSeriesData = [
    {
      name: "opportunity creation rate",
      data: seriesData,
    },
  ];
  const fullOptions: ApexOptions = {
    ...options,
    xaxis: {
      ...options.xaxis,
      categories: categories || [],
      labels: {
        style: {
          fontSize: "12px",
        },
      },
    },
    chart: {
      type: "bar",
      height: 350,
      id: "opportunityCreationRate",
    },
  };

  const handleExport = () => {
    if (!exportChart) return;

    const params = {
      metrics: "opportunityCreationRate",
      format: format?.value?.toLowerCase(),
    };
    exportChart(params, format.value === "PDF" ? params?.metrics : undefined);
  };
  return (
    <Box className="lead-cpl-wrapper-campaign oppurtunity-wrapper">
      <Box className="cpl-wrapper">
        <Box>
          <Box
            sx={{
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="header-text">Opportunity Creation Rate (%)</p>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Select
                options={[
                  { value: "PDF", label: "PDF" },
                  { value: "CSV", label: "CSV" },
                ]}
                isSearchable={false}
                value={format}
                onChange={(selectedOption: any) => setFormat(selectedOption)}
                className="select-dropdown"
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: (props) => {
                    return (
                      <components.DropdownIndicator {...props}>
                        <ArrowDropDownIcon
                          style={{ fontSize: 20, color: "black" }}
                        />
                      </components.DropdownIndicator>
                    );
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={handleExport}
                className="address-edit-button"
                disabled={!enableExport}
              >
                Export
              </Button>
            </Box>
          </Box>
          {!isLoading && seriesData?.length ? (
            <div
              style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                width: "800px",
                height: "400px",
                overflow: "hidden",
              }}
            >
              <Chart
                options={fullOptions}
                series={fullSeriesData}
                type="bar"
                height={400}
              />
            </div>
          ) : (
            <></>
          )}
          <Box sx={{ padding: "0px 20px" }}>
            {!isLoading && seriesData?.length ? (
              <Chart
                options={options}
                series={series}
                type="bar"
                height={350}
              />
            ) : (
              <Box className="no-data-found">
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    color: "#7B8A8B",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    margin: "40px 0",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {seriesData?.length ? "Loading..." : " No data available"}
                </p>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OpportunityCreationRate;
